import { createContext, useContext } from "react";

import type { CuratedPlaceOfSafety } from "@thalamos/common";

export type PlacesOfSafety = {
  placesOfSafety: CuratedPlaceOfSafety[];
  /**
   * True while the curated list is still being fetched for the session. Lets
   * pickers show a loading state instead of an empty list (which would tempt
   * the user into manual entry).
   */
  isLoading: boolean;
  /**
   * True when the curated list failed to load. Distinct from "no places exist"
   * so consumers can show an explicit failure state rather than a silent empty
   * list — the original failure mode this work set out to fix.
   */
  isError: boolean;
  /**
   * Re-attempts the session preload. Lets the UI offer a retry after a
   * transient failure, so a single dropped request does not strand the user in
   * manual entry for the rest of the session (the provider stays mounted, so
   * `isError` would otherwise be sticky).
   */
  refetch: () => void;
};

/**
 * This provides a mechanism for components to make queries about Places Of Safety.
 *
 * The list is fetched once per browsing session by `PlacesOfSafetyProvider`
 * (mounted high in the authenticated app shell) and shared via this context, so
 * individual pages do not refetch it on every navigation.
 */
export const PlacesOfSafetyContext = createContext<PlacesOfSafety>({
  /**
   * A list of places of safety that can be picked from.
   */
  placesOfSafety: [],
  isLoading: false,
  isError: false,
  refetch: () => {},
});

export const usePlacesOfSafety = ({
  filter,
}: {
  filter?: (place: CuratedPlaceOfSafety) => boolean;
}): PlacesOfSafety => {
  // Get the raw list from context
  const placesOfSafety = useContext(PlacesOfSafetyContext);

  // Filter as appropriate, preserving the loading/error flags
  if (filter) {
    return {
      ...placesOfSafety,
      placesOfSafety: placesOfSafety.placesOfSafety.filter(filter),
    };
  } else {
    return placesOfSafety;
  }
};
